import React, {useEffect, useState, useLayoutEffect} from 'react'
import i18n from 'i18next'
import actions from 'redux-auto'
import {connect, useSelector} from 'react-redux'
import PropTypes from 'prop-types'
import Headline from '#components/a/Headline'
import Loading from '#components/a/Loading'
import PageHeader from '#components/m/PageHeader'
import AppointmentsCell from '#components/m/Versetzungsplan/AppointmentsCell'
import Ueberplanungen from '#components/m/Versetzungsplan/Ueberplanungen'
import Button from '#components/a/Button'
import VersetzungsplanHeaderButtons from '#components/m/Versetzungsplan/VersetzungsplanHeaderButtons'
import {CalendarType, CalendarConfig} from '#tools/calendarConfig'
import Text from '#components/a/Text'
import Pagination from '#components/m/Pagination'
import useCurrentUser from '#hooks/store/useCurrentUser'
import AbteilungsEditDialog from '#components/m/Versetzungsplan/AbteilungsEditDialog'
import {LinkWithArrow} from '#components/m/MeineOrganisation/LinkWithArrow'
import {usePermissions} from '#hooks/usePermissions'
import {startOfWeek, startOfMonth, startOfYear} from 'ag-common/facades'

export const Versetzungsplan = props => {
  const {
    readVersetzungsplan,
    readVersetzungsplanTermine,
    setVisibleAzubis,
    isLoading,
    versetzungsplaene,
    ueberplanungen,
    setAzubiIdsToLoadTermine,
  } = props

  const [take, setTake] = useState(7)

  const {matchingAzubiIds} = useSelector(state => state.filter)
  const currentUser = useCurrentUser()
  const azubis =
    currentUser.role === 'azubi' ? [currentUser] : useSelector(state => state.azubis.azubisWithVersetzungsplanModul)
  const permissions = usePermissions()
  const visibleAzubis = useSelector(state => state.versetzungsplan.visibleAzubis)
  const azubiIdsToLoadTermine = useSelector(state => state.versetzungsplan.azubiIdsToLoadTermine)

  const [selectedAbteilungsEinsatz, setSelectedAbteilungsEinsatz] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const [calendarType, setCalendarType] = useState(CalendarType.WEEKLY)
  const calendarConfig = CalendarConfig[calendarType]
  const [date, setDate] = useState(calendarConfig.getInitialDate())

  useEffect(() => {
    if (permissions.versetzungsplan.termine.editAusbilderTermin) {
      if (azubis) {
        setAzubiIdsToLoadTermine({azubiIdsToLoadTermine: azubis.map(a => a.id)})
      } else {
        setAzubiIdsToLoadTermine({azubiIdsToLoadTermine: []})
      }
    }
  }, [azubis])

  useEffect(() => {
    if (currentUser.role !== 'azubi') {
      actions.azubis.getAzubisWithVersetzungsplanModul()
    }
  }, [])

  useEffect(() => {
    if (createDialogOpen === true) {
      setSelectedAbteilungsEinsatz(null)
    }
  }, [createDialogOpen])

  useEffect(() => {
    if (selectedAbteilungsEinsatz !== null) {
      setCreateDialogOpen(false)
    }
  }, [selectedAbteilungsEinsatz])

  const visibleAzubiIds = visibleAzubis ? visibleAzubis.map(a => a.id).join(',') : ''
  useEffect(() => {
    readVersetzungsplan()
    if (!permissions.versetzungsplan.termine.editAusbilderTermin) {
      if (visibleAzubis) {
        setAzubiIdsToLoadTermine({azubiIdsToLoadTermine: visibleAzubis.map(a => a.id)})
      } else {
        setAzubiIdsToLoadTermine({azubiIdsToLoadTermine: []})
      }
    }
  }, [visibleAzubiIds])

  useEffect(() => {
    readVersetzungsplanTermine()
  }, [azubiIdsToLoadTermine])

  const CalendarHeader = calendarConfig.headerComponent
  const startOfCalendar = calendarConfig.getStartOfCalendar(date)
  const endOfCalendar = calendarConfig.getEndOfCalendar(date)

  const filteredAzubis = currentUser.role === 'azubi' ? azubis : azubis?.filter(a => matchingAzubiIds.includes(a.id))
  const versetzungsplaeneEmpty = !filteredAzubis || filteredAzubis.length === 0

  const filteredAzubiIds = filteredAzubis ? filteredAzubis.map(a => a.id).join(',') : ''
  useEffect(() => {
    if (filteredAzubis) {
      scroll(0, take)
    }
  }, [filteredAzubiIds])

  const onScrollLeft = () => {
    setDate(calendarConfig.getPrevious(date))
  }

  const onScrollRight = () => {
    setDate(calendarConfig.getNext(date))
  }

  const handleClickYear = date => {
    setCalendarType(CalendarType.YEARLY)
    setDate(startOfYear(date))
  }

  const handleClickMonth = date => {
    setCalendarType(CalendarType.MONTHLY)
    setDate(startOfMonth(date))
  }

  const handleClickWeek = date => {
    setCalendarType(CalendarType.WEEKLY)
    setDate(startOfWeek(date))
  }

  const scroll = (skip, take) => {
    setVisibleAzubis({visibleAzubis: filteredAzubis.slice(skip, skip + take)})
  }

  function VersetzungsplanHeader() {
    const showRessourcenplanungsLink = permissions.ressourcenplanung.manage
    if (showRessourcenplanungsLink) {
      return (
        <div className="versetzungsplan-ausbilder--header">
          <Headline importance={2} modifier="section" text={i18n.t('versetzungsplan_page_title')} />
          <LinkWithArrow to="/ressourcenplanung" text={i18n.t('link_text_to_ressourcenplanung')} />
        </div>
      )
    }
    return <Headline importance={2} modifier="section" text={i18n.t('versetzungsplan_page_title')} />
  }

  useLayoutEffect(() => {
    function updateSize() {
      let value = document.documentElement.clientHeight - 640
      let azubiCount = Math.floor(value / 104)
      if (azubiCount < 3) {
        azubiCount = 3
      }
      setTake(azubiCount)
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  return (
    <div className="versetzungsplan">
      <PageHeader
        headline={<VersetzungsplanHeader />}
        button={
          !versetzungsplaeneEmpty && (
            <VersetzungsplanHeaderButtons
              setCreateDialogOpen={setCreateDialogOpen}
              createDialogOpen={createDialogOpen}
              azubis={azubis}
            />
          )
        }
      />
      {isLoading === true && <Loading />}
      {!isLoading && <div className={'spacer'} />}
      {isLoading === false && versetzungsplaeneEmpty && <Text text={i18n.t('no_azubi_with_this_module')} />}
      {!versetzungsplaeneEmpty && (
        <div className={`calendar calendar--${calendarType}`}>
          <CalendarHeader
            date={date}
            startOfCalendar={startOfCalendar}
            endOfCalendar={endOfCalendar}
            setDate={setDate}
            setCalendarType={setCalendarType}
            calendarType={calendarType}
            handleClickYear={handleClickYear}
            handleClickMonth={handleClickMonth}
            handleClickWeek={handleClickWeek}
            scrollLeftControl={() => (
              <button
                type="button"
                className="calendar__scrollbutton calendar__scrollbutton--left"
                onClick={onScrollLeft}
              />
            )}
            scrollRightControl={() => (
              <button
                type="button"
                className="calendar__scrollbutton calendar__scrollbutton--right"
                onClick={onScrollRight}
              />
            )}
            calendarTypeButtons={() => (
              <div className="calendar__calendar-type-buttons">
                <Button
                  text="Woche"
                  event={() => handleClickWeek(new Date())}
                  variant={calendarType === CalendarType.WEEKLY ? 'primary' : 'secondarydark'}
                />
                <Button
                  text="Monat"
                  event={() => handleClickMonth(new Date())}
                  variant={calendarType === CalendarType.MONTHLY ? 'primary' : 'secondarydark'}
                />
                <Button
                  text="Jahr"
                  event={() => handleClickYear(new Date())}
                  variant={calendarType === CalendarType.YEARLY ? 'primary' : 'secondarydark'}
                />
              </div>
            )}
          />
          {ueberplanungen && permissions.versetzungsplan.editAbteilungseinsaetze && (
            <Ueberplanungen
              ueberplanungen={ueberplanungen}
              startOfCalendar={startOfCalendar}
              endOfCalendar={endOfCalendar}
              calendarType={calendarType}
            />
          )}
          {visibleAzubis.map(a => (
            <AppointmentsCell
              azubi={a}
              key={a.id}
              calendarType={calendarType}
              startOfCalendar={startOfCalendar}
              endOfCalendar={endOfCalendar}
              setSelectedAbteilungsEinsatz={currentUser.role === 'ausbilder' ? setSelectedAbteilungsEinsatz : () => {}}
              editAbteilungseinsaetze={permissions.versetzungsplan.editAbteilungseinsaetze}
            />
          ))}
          <Pagination data={filteredAzubis} scrollCallback={scroll} className="light" take={take} />
          {selectedAbteilungsEinsatz !== null && (
            <AbteilungsEditDialog
              closeAbteilungDialog={() => setSelectedAbteilungsEinsatz(null)}
              selectedAbteilung={selectedAbteilungsEinsatz}
              versetzungsplaene={versetzungsplaene}
              azubis={azubis}
            />
          )}
        </div>
      )}
    </div>
  )
}

Versetzungsplan.propTypes = {
  isLoading: PropTypes.bool,
  versetzungsplaene: PropTypes.array,
  readVersetzungsplan: PropTypes.func.isRequired,
  readVersetzungsplanTermine: PropTypes.func.isRequired,
  setVisibleAzubis: PropTypes.func.isRequired,
  azubis: PropTypes.array,
}

function mapStateToProps(state) {
  const {isLoading, isLoadingTermine, versetzungsplaene, ueberplanungen} = state.versetzungsplan
  return {
    isLoading: isLoading || isLoadingTermine,
    versetzungsplaene,
    ueberplanungen,
  }
}

function mapDispatchToProps() {
  return {
    readVersetzungsplan: actions.versetzungsplan.readVersetzungsplan,
    readVersetzungsplanTermine: actions.versetzungsplan.readVersetzungsplanTermine,
    setVisibleAzubis: actions.versetzungsplan.setVisibleAzubis,
    setAzubiIdsToLoadTermine: actions.versetzungsplan.setAzubiIdsToLoadTermine,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Versetzungsplan)
